import { useState } from "react";
import { CreatorScoreDetail } from "../score/CreatorScoreDetail";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ExternalLink,
  MessageSquare,
  Send,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/marketing/lib/utils";
import { Button } from "@/marketing/components/ui/button";
import { Sheet, SheetContent } from "@/marketing/components/ui/sheet";
import { Input } from "@/marketing/components/ui/input";
import {
  type CampaignInfluencer,
  type AddContentSubmissionRequest,
  REJECTED_DOT,
  REJECTED_PILL,
} from "@/marketing/models/campaignInfluencer";
import type { CampaignPipelineDefinition } from "@/marketing/models/campaignPipeline";
import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";
import { canTransition, getStageLabel } from "@/marketing/lib/campaignPipeline";
import { getStageToneStyle } from "@/marketing/lib/pipelineTone";
import { CampaignActivityKind } from "@/marketing/models/campaignActivity";
import { campaignPipelineAPIClient } from "@/marketing/infrastructure/influencer/campaign/pipeline/campaignPipelineAPIClient";
import { campaignActivityAPIClient } from "@/marketing/infrastructure/influencer/campaign/activity/campaignActivityAPIClient";
import { applyQuiet } from "@/marketing/infrastructure/influencer/campaign/pipeline/utils/applyQuiet";
import { CAMPAIGN_PIPELINE_KEYS } from "@/marketing/infrastructure/influencer/campaign/pipeline/utils/keys";
import { USER_QUERIES } from "@/marketing/infrastructure/user/utils/queries";
import { CAMPAIGN_ACTIVITY_KEYS } from "@/marketing/infrastructure/influencer/campaign/activity/utils/keys";
import { SCOUTING_KEYS } from '@/marketing/infrastructure/influencer/campaign/scouting/queries';
import { CAMPAIGN_ACTIVITY_QUERIES } from "@/marketing/infrastructure/influencer/campaign/activity/utils/queries";
import { useAuthStore } from "@/marketing/stores/useAuthStore";
import { useCampaignQuietStore } from "@/marketing/stores/useCampaignQuietStore";
import { formatDate } from "@/marketing/lib/formatters";
import { getActivityAuthorLabel } from "@/marketing/lib/activityAuthor";
import { getStageTransitionCauseLabel } from "@/marketing/lib/stageTransitionCause";
import { getPlatformLabel, getSocialMediaUrl } from "@/marketing/lib/socialMediaUrls";

import { TikTokEmbed } from "./TikTokEmbed";
import { ContentPosts } from "./ContentPosts";
import { AddContentSubmissionForm } from "./AddContentSubmissionForm";
import { invalidateContentQueries } from "../contentTracking/contentTrackingCache";
import { ContactSection } from "./ContactSection";
import { CampaignInfluencerTagSection } from "../tags/CampaignInfluencerTagSection";
import { ProductAssignmentSection } from "./ProductAssignmentSection";

type SlideTab = "details" | "activity";

interface InfluencerSlideOverProps {
  campaignId: string;
  campaign: InfluencerCampaign;
  pipelineDefinition: CampaignPipelineDefinition;
  influencer: CampaignInfluencer | null;
  onClose: () => void;
  onAdvanceToNext?: (() => void) | undefined;
}

export function InfluencerSlideOver({
  campaignId,
  campaign,
  pipelineDefinition,
  influencer,
  onClose,
  onAdvanceToNext,
}: InfluencerSlideOverProps) {
  const open = influencer !== null;
  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent className="tw-w-[480px] tw-overflow-hidden tw-p-0 sm:tw-max-w-[480px]">
        {influencer && (
          <SlideOverBody
            key={influencer.influencerId}
            campaignId={campaignId}
            campaign={campaign}
            pipelineDefinition={pipelineDefinition}
            influencer={influencer}
            onClose={onClose}
            onAdvanceToNext={onAdvanceToNext}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function SlideOverBody({
  campaignId,
  campaign,
  pipelineDefinition,
  influencer,
  onClose,
  onAdvanceToNext,
}: {
  campaignId: string;
  campaign: InfluencerCampaign;
  pipelineDefinition: CampaignPipelineDefinition;
  influencer: CampaignInfluencer;
  onClose: () => void;
  onAdvanceToNext?: (() => void) | undefined;
}) {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const transitionBy = currentUser?.email ?? "unknown";

  const [tab, setTab] = useState<SlideTab>("details");
  const [comment, setComment] = useState("");

  const handle = influencer.handle ?? influencer.influencerId.slice(0, 8);
  const initials =
    handle
      .replace(/[^a-zA-Z]/g, "")
      .slice(0, 2)
      .toUpperCase() || "??";

  const stages = pipelineDefinition.stages;
  const stageIdx = stages.findIndex((s) => s.stage === influencer.pipelineStage);
  const isRejected = influencer.rejected;
  const neighborStage = (offset: -1 | 1): string | null => {
    const neighbor = stageIdx >= 0 ? stages[stageIdx + offset] : undefined;
    if (!neighbor) return null;
    const { allowed } = canTransition(
      pipelineDefinition,
      influencer.pipelineStage,
      neighbor.stage,
    );
    return allowed ? neighbor.stage : null;
  };
  const prevStage = neighborStage(-1);
  const nextStage = neighborStage(1);

  const transitionMutation = useMutation({
    mutationFn: ({ toStage }: { toStage: string }) =>
      campaignPipelineAPIClient.transitionCampaignInfluencerStage(
        campaignId,
        influencer.influencerId,
        applyQuiet(
          { toStage, transitionBy },
          useCampaignQuietStore.getState().isQuiet(campaignId),
        ),
      ),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: CAMPAIGN_PIPELINE_KEYS.detail(campaignId, influencer.influencerId) });
      void queryClient.invalidateQueries({ queryKey: SCOUTING_KEYS.list(campaignId) });
      void queryClient.invalidateQueries({
        queryKey: CAMPAIGN_PIPELINE_KEYS.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: CAMPAIGN_ACTIVITY_KEYS.all,
      });
      toast.success(
        `Moved to ${getStageLabel(pipelineDefinition, vars.toStage)}`,
      );
      onAdvanceToNext?.();
    },
    onError: (err: unknown) => toast.error(extractErrorMessage(err)),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ rejected }: { rejected: boolean }) =>
      campaignPipelineAPIClient.setCampaignInfluencerRejected(
        campaignId,
        influencer.influencerId,
        {
          rejected,
          transitionBy,
        },
      ),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: CAMPAIGN_PIPELINE_KEYS.detail(campaignId, influencer.influencerId) });
      void queryClient.invalidateQueries({ queryKey: SCOUTING_KEYS.list(campaignId) });
      void queryClient.invalidateQueries({
        queryKey: CAMPAIGN_PIPELINE_KEYS.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: CAMPAIGN_ACTIVITY_KEYS.all,
      });
      toast.success(
        vars.rejected ? "Marked rejected" : "Restored from rejected",
      );
      onAdvanceToNext?.();
    },
    onError: (err: unknown) => toast.error(extractErrorMessage(err)),
  });

  const commentMutation = useMutation({
    mutationFn: (body: string) =>
      campaignActivityAPIClient.addCampaignInfluencerComment(
        campaignId,
        influencer.influencerId,
        {
          body,
          authorId: transitionBy,
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CAMPAIGN_PIPELINE_KEYS.detail(campaignId, influencer.influencerId) });
      setComment("");
      void queryClient.invalidateQueries({
        queryKey: CAMPAIGN_ACTIVITY_KEYS.all,
      });
      toast.success("Comment posted");
    },
    onError: (err: unknown) => toast.error(extractErrorMessage(err)),
  });

  const submissions = influencer.contentSubmissions ?? [];
  const submissionUrls = submissions.map((submission) => submission.url);
  const invalidateContent = () => Promise.all([
    invalidateContentQueries(queryClient),
    queryClient.invalidateQueries({ queryKey: CAMPAIGN_PIPELINE_KEYS.detail(campaignId, influencer.influencerId) }),
  ]);

  const addSubmissionMutation = useMutation({
    mutationFn: (request: AddContentSubmissionRequest) =>
      campaignPipelineAPIClient.addCampaignInfluencerContentSubmission(
        campaignId,
        influencer.influencerId,
        request,
      ),
    onSuccess: async () => {
      await invalidateContent();
      toast.success("Content links updated");
    },
    onError: (err: unknown) => toast.error(extractErrorMessage(err)),
  });

  const deleteSubmissionMutation = useMutation({
    mutationFn: (url: string) =>
      campaignPipelineAPIClient.deleteCampaignInfluencerContent(campaignId, influencer.influencerId, { url, transitionBy }),
    onSuccess: async () => {
      await invalidateContent();
      toast.success("Content link removed");
    },
    onError: (err: unknown) => toast.error(extractErrorMessage(err)),
  });

  const sparkAdsCodeMutation = useMutation({
    mutationFn: ({ url, sparkAdsCode }: { url: string; sparkAdsCode: string }) =>
      campaignPipelineAPIClient.setCampaignInfluencerContentSparkAdsCode(campaignId, influencer.influencerId, {
        url,
        sparkAdsCode,
        transitionBy,
      }),
    onSuccess: async () => {
      await invalidateContent();
      toast.success("Spark Ads code updated");
    },
    onError: (err: unknown) => toast.error(extractErrorMessage(err)),
  });

  const contactQuery = useQuery({
    ...USER_QUERIES.contact(influencer.userId),
    enabled: tab === "details" && influencer.userId != null,
  });

  const timelineQuery = useQuery({
    ...CAMPAIGN_ACTIVITY_QUERIES.influencerTimeline(
      campaignId,
      influencer.influencerId,
    ),
    enabled: tab === "activity",
  });

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      {/* Header */}
      <div className="tw-flex tw-items-start tw-justify-between tw-border-b tw-border-border tw-p-5 tw-pr-12">
        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
          <div className="tw-flex tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-muted tw-text-[12px] tw-font-semibold tw-text-foreground/70">
            {initials}
          </div>
          <div className="tw-min-w-0">
            {(() => {
              const profileUrl = influencer.platform
                ? getSocialMediaUrl(influencer.platform, handle)
                : "";
              const platformLabel = influencer.platform
                ? getPlatformLabel(influencer.platform)
                : "this influencer";
              return profileUrl ? (
                <a
                  href={profileUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={`Open @${handle} on ${platformLabel}`}
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-truncate tw-text-base tw-font-semibold tw-leading-tight tw-text-foreground hover:tw-text-blue-700 hover:tw-underline"
                >
                  @{handle}
                  <ExternalLink className="tw-h-3.5 tw-w-3.5 tw-opacity-60" />
                </a>
              ) : (
                <div className="tw-truncate tw-text-base tw-font-semibold tw-leading-tight">
                  @{handle}
                </div>
              );
            })()}
            <div className="tw-truncate tw-text-[12px] tw-text-muted-foreground">
              {influencer.influencerId}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tw-flex tw-h-9 tw-items-center tw-border-b tw-border-border tw-px-4">
        {(["details", "activity"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              "tw-relative tw-inline-flex tw-h-9 tw-items-center tw-gap-1.5 tw-px-3 tw-text-[13px] tw-font-medium",
              tab === t
                ? "tw-text-foreground"
                : "tw-text-muted-foreground hover:tw-text-foreground",
              tab === t &&
                "after:tw-absolute after:tw-bottom-0 after:tw-left-3 after:tw-right-3 after:tw-h-0.5 after:tw-rounded-full after:tw-bg-blue-600",
            )}
          >
            {t === "details" ? "Details" : "Activity"}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="tw-flex-1 tw-overflow-y-auto tw-p-5">
        {tab === "details" && (
          <div className="tw-flex tw-flex-col tw-gap-5">
            <details className="tw-rounded tw-border tw-border-border tw-p-3">
              <summary className="tw-cursor-pointer tw-text-sm tw-font-semibold">Creator Score · View breakdown</summary>
              <CreatorScoreDetail campaignId={campaignId} creator={influencer} />
            </details>
            {/* TikTok embed (only when handle exists and platform is tiktok or unset) */}
            {influencer.handle &&
              (!influencer.platform ||
                influencer.platform.toLowerCase() === "tiktok") && (
                <TikTokEmbed handle={influencer.handle} />
              )}

            {/* Stage stepper */}
            <Section title="Pipeline stage">
              <div className="tw-mb-2 tw-flex tw-flex-wrap tw-gap-1.5">
                {stages.map(({ stage, labels }, i) => (
                  <span
                    key={stage}
                    className={cn(
                      "tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px]",
                      isRejected
                        ? "tw-bg-muted tw-text-muted-foreground"
                        : i < stageIdx
                          ? "tw-bg-emerald-100 tw-text-emerald-700"
                          : i === stageIdx
                            ? cn(
                              getStageToneStyle(pipelineDefinition, stage)
                                .pill,
                              "tw-font-semibold",
                            )
                            : "tw-bg-muted tw-text-muted-foreground",
                    )}
                  >
                    {!isRejected && i < stageIdx && (
                      <Check className="tw-h-3 tw-w-3" />
                    )}
                    {labels.admin}
                  </span>
                ))}
                {isRejected && (
                  <span
                    className={cn(
                      "tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold",
                      REJECTED_PILL,
                    )}
                  >
                    <span
                      className={cn("tw-h-1.5 tw-w-1.5 tw-rounded-full", REJECTED_DOT)}
                    />
                    Rejected
                  </span>
                )}
              </div>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                {!isRejected && prevStage && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="tw-gap-1.5"
                    disabled={transitionMutation.isPending}
                    onClick={() =>
                      transitionMutation.mutate({ toStage: prevStage })
                    }
                  >
                    <ArrowLeft className="tw-h-3 tw-w-3" /> Move back
                  </Button>
                )}
                {!isRejected && nextStage && (
                  <Button
                    size="sm"
                    className="tw-gap-1.5"
                    disabled={transitionMutation.isPending}
                    onClick={() =>
                      transitionMutation.mutate({ toStage: nextStage })
                    }
                  >
                    Advance to {getStageLabel(pipelineDefinition, nextStage)}
                    <ArrowRight className="tw-h-3 tw-w-3" />
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className={isRejected ? undefined : "tw-text-rose-700"}
                  disabled={rejectMutation.isPending}
                  onClick={() =>
                    rejectMutation.mutate({ rejected: !isRejected })
                  }
                >
                  {isRejected ? "Restore" : "Reject"}
                </Button>
              </div>
            </Section>

            {/* Profile snapshot */}
            <Section title="Profile">
              <dl className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-2 tw-text-[12px]">
                <FieldRow label="Platform" value={influencer.platform ?? "—"} />
                <FieldRow label="Handle" value={`@${handle}`} />
                <FieldRow
                  label="Joined"
                  value={formatDate(influencer.joinedAt)}
                />
                <FieldRow
                  label="Last activity"
                  value={
                    influencer.lastTransitionAt
                      ? formatDate(influencer.lastTransitionAt)
                      : "—"
                  }
                />
                <FieldRow
                  label="Shipping tracking"
                  value={influencer.shippingTracking ?? "—"}
                />
                <FieldRow
                  label="Content URLs"
                  value={String(submissions.length)}
                />
              </dl>
            </Section>

            <Section title="Tags">
              <CampaignInfluencerTagSection
                campaignId={campaignId}
                influencer={influencer}
              />
            </Section>

            {/* Product selection (choice-reward only): assigned products, or an Assign button */}
            <ProductAssignmentSection
              campaign={campaign}
              influencer={influencer}
            />

            {/* Creator Hub contact (email, phone, shipping address resolved by linked userId) */}
            <Section title="Contact & shipping">
              <ContactSection
                contact={influencer.userId == null ? null : contactQuery.data}
                isLoading={influencer.userId != null && contactQuery.isLoading}
                isError={contactQuery.isError}
              />
            </Section>

            {/* Content links (editable — backfill links collected outside the hub) */}
            <Section title="Content links">
              <div className="tw-flex tw-flex-col tw-gap-2">
                {submissionUrls.length > 0 ? (
                  <ul className="tw-flex tw-flex-col tw-gap-1.5">
                    {submissionUrls.map((url) => (
                      <li key={url} className="tw-flex tw-items-center tw-gap-2">
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="tw-flex-1 tw-truncate tw-text-[12px] tw-text-blue-700 hover:tw-underline"
                        >
                          {url}
                        </a>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="tw-h-7 tw-w-7 tw-flex-shrink-0 tw-p-0 tw-text-muted-foreground hover:tw-text-rose-700"
                          title="Remove link"
                          disabled={addSubmissionMutation.isPending || deleteSubmissionMutation.isPending}
                          onClick={() => deleteSubmissionMutation.mutate(url)}
                        >
                          <X className="tw-h-3.5 tw-w-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="tw-text-[12px] tw-text-muted-foreground">
                    No content links yet. Add links collected outside the hub.
                  </p>
                )}
                <AddContentSubmissionForm
                  existingUrls={submissionUrls}
                  disabled={addSubmissionMutation.isPending || deleteSubmissionMutation.isPending}
                  onAdd={(submission) => addSubmissionMutation.mutateAsync({ submission, transitionBy })}
                />
              </div>
            </Section>

            {/* Platform embeds for submitted content */}
            {submissionUrls.length > 0 && (
              <Section title="Preview">
                <ContentPosts
                  submissions={influencer.contentSubmissions}
                  onSaveSparkAdsCode={(url, sparkAdsCode) => sparkAdsCodeMutation.mutate({ url, sparkAdsCode })}
                  savingUrl={sparkAdsCodeMutation.isPending ? sparkAdsCodeMutation.variables?.url : undefined}
                />
              </Section>
            )}

            {/* Comment composer */}
            <Section title="Add comment">
              <div className="tw-flex tw-items-end tw-gap-2">
                <Input
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a note for this influencer…"
                  className="tw-h-9"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && comment.trim().length > 0) {
                      e.preventDefault();
                      commentMutation.mutate(comment.trim());
                    }
                  }}
                />
                <Button
                  size="sm"
                  className="tw-gap-1.5"
                  disabled={
                    comment.trim().length === 0 || commentMutation.isPending
                  }
                  onClick={() => commentMutation.mutate(comment.trim())}
                >
                  <Send className="tw-h-3.5 tw-w-3.5" />
                  Post
                </Button>
              </div>
            </Section>
          </div>
        )}

        {tab === "activity" && (
          <div className="tw-flex tw-flex-col tw-gap-3">
            {timelineQuery.isLoading && (
              <div className="tw-text-center tw-text-sm tw-text-muted-foreground">
                Loading activity…
              </div>
            )}
            {timelineQuery.isError && (
              <div className="tw-text-center tw-text-sm tw-text-red-600">
                Failed to load activity.
              </div>
            )}
            {timelineQuery.data?.length === 0 && (
              <div className="tw-rounded-md tw-border tw-border-dashed tw-border-border tw-p-6 tw-text-center tw-text-sm tw-text-muted-foreground">
                No activity yet for this influencer.
              </div>
            )}
            {timelineQuery.data?.map((entry) => (
              <div
                key={entry.logId}
                className="tw-rounded-md tw-border tw-border-border tw-bg-card tw-p-3"
              >
                <div className="tw-flex tw-items-center tw-justify-between tw-text-[11px] tw-text-muted-foreground">
                  <span className="tw-font-medium tw-text-foreground">
                    {getActivityAuthorLabel(entry)}
                  </span>
                  <span>{formatDate(entry.createdAt)}</span>
                </div>
                {entry.body && (
                  <div
                    className={cn(
                      "tw-mt-1.5 tw-text-[13px] tw-leading-relaxed",
                      entry.kind === CampaignActivityKind.Comment &&
                        "tw-text-foreground",
                    )}
                  >
                    {entry.body}
                  </div>
                )}
                {entry.fromStage && entry.toStage && (
                  <div className="tw-mt-1.5 tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-text-muted-foreground">
                    <span className="tw-rounded tw-bg-muted tw-px-1.5 tw-py-0.5">
                      {entry.fromStage}
                    </span>
                    <ArrowRight className="tw-h-3 tw-w-3" />
                    <span className="tw-rounded tw-bg-blue-100 tw-px-1.5 tw-py-0.5 tw-text-blue-700">
                      {entry.toStage}
                    </span>
                    {getStageTransitionCauseLabel(entry) && (
                      <>
                        <span>·</span>
                        <span>{getStageTransitionCauseLabel(entry)}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="tw-border-t tw-border-border tw-p-3">
        <Button
          variant="outline"
          size="sm"
          className="tw-w-full"
          onClick={onClose}
        >
          Close
        </Button>
        <div className="tw-mt-2 tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-[10px] tw-text-muted-foreground">
          <MessageSquare className="tw-h-3 tw-w-3" />
          Messages thread ships in v1.1
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="tw-mb-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="tw-text-muted-foreground">{label}</dt>
      <dd className="tw-truncate tw-font-medium tw-text-foreground">{value}</dd>
    </>
  );
}

function extractErrorMessage(err: unknown): string {
  if (typeof err === "object" && err !== null) {
    const response = (err as { response?: { data?: { message?: string } } })
      .response;
    if (response?.data?.message) return response.data.message;
    const message = (err as { message?: string }).message;
    if (message) return message;
  }
  return "Action failed";
}
